/* eslint-disable max-len  */
/**
 * A function to handle a click on a checkout button. This function uses the eventCallback
 * data layer variable to handle navigation after the ecommerce data has been sent to
 * Google Analytics.
 * @param {array} products
 * @param {checkOutURL} string
 */
export const gtmCheckout = (products, checkOutURL) => {
	const cartItems = products.map(item => {
		if (item.variant) {
			return {
				name: item.title,
				id: item.variant?.product?.id.split('/').pop(),
				price: item.variant?.price?.amount,
				brand: 'Balanced Body',
				productType: item.variant?.product?.productType,
				category: '',
				variant: item.variant?.title,
				variantId: item.variant?.id.split('/').pop(),
				quantity: item.quantity,
			};
		}
		return {};
	});

	const dataLayer = window.dataLayer || [];
	dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
	dataLayer.push({
		event: 'checkout',
		ecommerce: {
			checkout: {
				actionField: {step: 1, option: 'Clicked Review Order & Checkout'},
				products: cartItems,
			},
		},
		// eventCallback() {
		// 	window.open(checkOutURL, '_self');
		// },
	});
	checkOutURL && window.open(checkOutURL, '_self');
};

/**
 *  Measure adding a product to a shopping cart by using an 'add' actionFieldObject
 *  and a list of productFieldObjects.
 * @param {array} products
 */
export const gtmAddToCart = items => {
	const gtmItems = items.map(item => {
		return {
			name: item.productTitle,
			id: item.productId.split('/').pop(),
			price: item.price?.amount,
			brand: 'Balanced Body',
			productType: item.productType,
			category: '',
			variant: item.variantTitle,
			variantId: item.variantId?.split('/').pop(),
			quantity: item.quantity,
		};
	});

	const dataLayer = window.dataLayer || [];
	dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
	dataLayer.push({
		event: 'addToCart',
		ecommerce: {
			currencyCode: 'USD',
			add: {
				products: gtmItems,
			},
		},
	});
};

/**
 *  Measure removing a product from a shopping cart by using an 'removeFromCart' actionFieldObject
 *  and a list of productFieldObjects.
 * @param {array} products
 */
export const gtmRemoveFromCart = product => {
	if (product.variant) {
		const dataLayer = window.dataLayer || [];
		dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
		dataLayer.push({
			event: 'removeFromCart',
			ecommerce: {
				remove: { // 'remove' actionFieldObject measures.
					products: [{ //  removing a product to a shopping cart.
						name: product.variant.product.title,
						id: product.variant?.product?.id.split('/').pop(),
						price: product.variant?.price?.amount,
						brand: 'Balanced Body',
						productType: product.variant?.product?.productType,
						category: '',
						variant: product.variant?.title,
						quantity: product.quantity,
					}],
				},
			},
		});
	}
};

/**
 *  Measure a view of product details. This example assumes the detail view occurs on pageload,
 *  and also tracks a standard pageview of the details page.
 * @param {object} product
 */
export const gtmProductView = product => {
	const dataLayer = window.dataLayer || [];
	const variantIds = product.variants
		?.map(variant => variant.shopifyId?.split('/').pop() || variant.storefrontId?.split('/').pop())
		.filter(Boolean) || [];
	dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
	dataLayer.push({
		ecommerce: {
			detail: {
				actionField: {list: 'PDP'}, // 'detail' actions have an optional list property.
				products: [{
					name: product.title, // Name or ID is required.
					id: product.shopifyId?.split('/').pop(),
					variantId: variantIds,
					price: product.priceRangeV2.minVariantPrice.amount,
					brand: 'Balanced Body',
				}],
			},
		},
	});
};

/**
 *  Measures product impressions and also tracks a standard
 *  pageview for the tag configuration.
 *  Product impressions are sent by pushing an impressions object
 *  containing one or more impressionFieldObjects.
 * @param {array} product
 * @param {string} location
 */
export const gtmProductImpression = (products, location, title) => {
	const items = products.map((item, index) => {
		// Needed for search pages
		const itemPrice = item.dataPoints?.find(dataPoint => { return dataPoint.key.toLowerCase() === 'product price numeric'; });

		return {
			name: item.title || item.name,
			id: item.shopifyId?.split('/').pop() || item.name,
			price: item.priceRangeV2?.minVariantPrice?.amount || itemPrice?.value,
			brand: 'Balanced Body',
			category: title,
			list: location,
			position: index,
		};
	});

	const dataLayer = window.dataLayer || [];
	dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
	dataLayer.push({
		ecommerce: {
			currencyCode: 'USD', // Local currency is optional.
			impressions: items,
		},
	});
};

/**
 *  Call this function when a user clicks on a product link. This function uses the event
 *  callback datalayer variable to handle navigation after the ecommerce data has been sent
 *  to Google Analytics.
 * @param {string} productName
 * @param {string} productPrice
 * @param {string} location
 */
export const gtmProductClick = (product, location, categoryTitle) => {
	const itemPrice = product.dataPoints?.find(dataPoint => { return dataPoint.key.toLowerCase() === 'product price numeric'; });
	const dataLayer = window.dataLayer || [];
	dataLayer.push({
		event: 'productClick',
		ecommerce: {
			click: {
				actionField: {list: location}, // Optional list property.
				products: [{
					name: product.title || product.name, // Name or ID is required.
					id: product.shopifyId?.split('/').pop() || product.uniqueId || product.name,
					price: product.priceRange?.minVariantPrice?.amount
						|| product.priceRangeV2?.minVariantPrice?.amount || itemPrice?.value || product.price,
					brand: 'Balanced Body',
					category: categoryTitle,
				}],
			},
		},
	});
};
